/**
 * Confirmación de que una subsanación quedó realmente guardada.
 *
 * Subsanar son dos operaciones contra servicios distintos y sin transacción entre ellas: el archivo
 * se sube al backend de documentos y queda en disco de inmediato, y solo después se enlaza a la
 * postulación. Si ese segundo paso no cuaja —o si otra pantalla sobrescribe la columna
 * `documentos_administrativos` con una copia vieja— el archivo existe y se puede descargar, pero la
 * postulación no queda subsanada y no hay trazabilidad.
 *
 * Eso fue lo que pasó en el caso C466-013: la participante subsanó, descargó su archivo y quedó
 * convencida de haber cumplido, cuando en base de datos no había quedado nada.
 *
 * Por eso, después de guardar hay que releer la postulación desde el servidor y comprobar contra lo
 * que el servidor devuelve, nunca contra lo que el navegador cree que mandó.
 */

export type MotivoFallo =
  | 'sin-respuesta'
  | 'sin-documentos'
  | 'documento-inexistente'
  | 'archivo-no-enlazado'
  | 'sin-trazabilidad';

export type ResultadoConfirmacion = {
  confirmada: boolean;
  motivo?: MotivoFallo;
  /** Explicación corta para el registro técnico, no para el participante. */
  detalle: string;
};

const texto = (valor: any): string => String(valor ?? '').trim();

/**
 * Comprueba, sobre los documentos tal como los devolvió el servidor, que el documento en `indice`
 * quedó enlazado al archivo `archivoEsperado` y con sus datos de trazabilidad.
 */
export const confirmarSubsanacion = (
  documentosDelServidor: any,
  indice: number,
  archivoEsperado: string
): ResultadoConfirmacion => {
  if (documentosDelServidor === null || documentosDelServidor === undefined) {
    return { confirmada: false, motivo: 'sin-respuesta', detalle: 'El servidor no devolvió la postulación.' };
  }

  if (!Array.isArray(documentosDelServidor)) {
    return { confirmada: false, motivo: 'sin-documentos', detalle: 'La postulación no trae documentos administrativos.' };
  }

  const doc = documentosDelServidor[indice];
  if (!doc) {
    return {
      confirmada: false,
      motivo: 'documento-inexistente',
      detalle: `No existe el documento en la posición ${indice}; la postulación trae ${documentosDelServidor.length}.`,
    };
  }

  if (texto(doc.url_participante) !== texto(archivoEsperado)) {
    return {
      confirmada: false,
      motivo: 'archivo-no-enlazado',
      detalle: `El documento quedó enlazado a "${texto(doc.url_participante) || '(vacío)'}" y no a "${archivoEsperado}".`,
    };
  }

  // El archivo puede estar enlazado y aun así haberse perdido el rastro de quién subsanó y cuándo,
  // que es justamente lo que deja sin defensa al participante ante un reclamo.
  if (!doc.checksubsanado || !texto(doc.fecha_subsanado) || !texto(doc.subsanado_por)) {
    return {
      confirmada: false,
      motivo: 'sin-trazabilidad',
      detalle: `El archivo quedó enlazado pero sin trazabilidad (checksubsanado=${!!doc.checksubsanado}, fecha="${texto(doc.fecha_subsanado)}", por="${texto(doc.subsanado_por)}").`,
    };
  }

  return { confirmada: true, detalle: 'Subsanación confirmada contra el servidor.' };
};

/**
 * Mensaje para el participante cuando la subsanación no quedó. Tiene que ser inequívoco: el daño del
 * caso C466-013 fue que la persona creyó haber subsanado.
 */
export const mensajeSubsanacionIncorrecta = (archivo: string): string =>
  'Tu documento se cargó, pero la subsanación NO quedó registrada. ' +
  'Esto significa que tu propuesta sigue pendiente de subsanar. ' +
  `Vuelve a intentarlo; si el problema se repite, reporta este dato al soporte: ${archivo}`;
